import { Alert, Button, Form, Input, Typography } from 'antd';
import {
  BankOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { useAuthStore } from '@/store/auth';
import { getDefaultLandingPath } from '@/store/prefs';
import { branding } from '@/config/branding';
import { BrandMark } from '@/shared/components/brand-mark';
import { useSchoolTheme } from '@/themes/use-theme';
import { login } from '@/api/auth';

const schema = z.object({
  username: z.string().min(1, 'Username required'),
  password: z.string().min(1, 'Password required'),
});

type LoginInput = z.infer<typeof schema>;

const HIGHLIGHTS = [
  { icon: <TeamOutlined />, label: 'Admissions & student records' },
  { icon: <WalletOutlined />, label: 'Fees, invoicing & receipts' },
  { icon: <BankOutlined />, label: 'Payroll, HR & finance ledger' },
];

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [serverError, setServerError] = useState<string | null>(null);
  const theme = useSchoolTheme();

  const primary = (theme.config.token?.colorPrimary as string) ?? '#2563eb';
  const secondary = theme.swatch[2] ?? primary;
  const cssVars = {
    ['--brand-primary' as string]: primary,
    ['--brand-secondary' as string]: secondary,
  };

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginInput>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = async (values: LoginInput) => {
    setServerError(null);
    try {
      const res = await login(values.username, values.password);
      setSession({
        token: res.accessToken,
        refreshToken: res.refreshToken,
        user: {
          id: res.user.id,
          username: res.user.username,
          fullName: res.user.username,
          role: res.user.role,
          roleId: res.user.roleId,
          staffId: res.user.staffId,
          mustChangePassword: res.user.mustChangePassword,
          permissions: res.user.permissions,
          scope: res.user.scope,
        },
      });
      navigate(res.user.mustChangePassword ? '/change-password' : getDefaultLandingPath(), { replace: true });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? 'Could not sign in. Please try again.';
      setServerError(message);
    }
  };

  return (
    <div className="login-shell" style={cssVars}>
      {/* Brand panel — hidden on narrow screens */}
      <aside className="login-brand">
        <div className="login-brand-glow" aria-hidden />
        <div className="login-brand-content">
          <div className="login-brand-head">
            <BrandMark className="login-brand-mark" />
            <div>
              <Typography.Title level={3} style={{ color: '#fff', margin: 0, lineHeight: 1.15 }}>
                {branding.name}
              </Typography.Title>
              {(branding.locality || branding.tagline) && (
                <Typography.Text style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {branding.tagline || branding.locality}
                </Typography.Text>
              )}
            </div>
          </div>

          <ul className="login-highlights">
            {HIGHLIGHTS.map((h) => (
              <li key={h.label}>
                <span className="login-highlight-icon">{h.icon}</span>
                {h.label}
              </li>
            ))}
          </ul>

          <div className="login-brand-foot">
            <SafetyCertificateOutlined />
            <span>On-premise · secured for staff use only</span>
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="login-form-pane">
        <div className="login-card">
          <div className="login-card-brand">
            <BrandMark />
            <Typography.Text strong style={{ fontSize: 16 }}>{branding.shortName}</Typography.Text>
          </div>

          <Typography.Title level={2} style={{ margin: '0 0 4px' }}>Welcome back</Typography.Title>
          <Typography.Text type="secondary">Sign in to your account to continue.</Typography.Text>

          {serverError && (
            <Alert type="error" showIcon message={serverError} style={{ margin: '20px 0 0' }} />
          )}

          <Form layout="vertical" onFinish={handleSubmit(onSubmit)} style={{ marginTop: 24 }} requiredMark={false}>
            <Form.Item label="Username" validateStatus={errors.username ? 'error' : ''} help={errors.username?.message}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <Input {...field} size="large" autoFocus autoComplete="username" prefix={<UserOutlined />} placeholder="Enter your username" />
                )}
              />
            </Form.Item>
            <Form.Item label="Password" validateStatus={errors.password ? 'error' : ''} help={errors.password?.message}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <Input.Password {...field} size="large" autoComplete="current-password" prefix={<LockOutlined />} placeholder="Enter your password" />
                )}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={isSubmitting}>
              Sign in
            </Button>
          </Form>

          <div className="login-foot">
            {[branding.locality, 'v0.1.0'].filter(Boolean).join(' · ')}
          </div>
        </div>
      </main>
    </div>
  );
}
